import React from 'react';
import * as THREE from 'three';
import Model from '@src/components/side-bar-menu/nft-assets/Model';
import SpherePositionPointAsset from './SpherePositionPointAsset';
import { PositionalAudio } from '@react-three/drei';
import { isValidURL } from '../../../../util/helpers';
import { ImageMaterial } from './imageMaterial';
import { VideoMaterial } from './videoMaterial';
import { ILight } from './lightTemplate';
import { LightAsset } from './lightAsset';
import { S3_BUCKET, CDN } from '@src/components/scene/assets/baseUrl';
import { convertMMLToThreeJS } from '@src/utils/mml';
import { STUDIO_ID_SUFFIX, ASSETS_2022_CDN } from '../../../../util/constants';
import { WaterShader } from './WaterShader';

export const ASSET_TYPE = {
  GLTF: {
    TELEPORTFLOOR: 'TELEPORTFLOOR',
    HIGHPACK: 'HIGHPACK',
    SIGNPACK: 'SIGNPACK',
    ASSETPACK: 'ASSETPACK',
    OTHER: 'OTHER',
  },
  PRIMITIVE: {
    BOX: 'BOX',
    SPHERE: 'SPHERE',
    POSITION_POINT: 'POSITION_POINT',
  },
  MISC: {
    POSITIONAL_AUDIO: 'POSITIONAL_AUDIO',
    LIGHT: 'LIGHT',
    PORTAL: 'PORTAL',
  },
  PRIMITIVE_TEXTURE: {
    IMAGE_PLANE: 'IMAGE_PLANE',
    VIDEO_PLANE: 'VIDEO_PLANE',
    SCREEN_SHARE: 'SCREEN_SHARE',
    STREAM: 'STREAM',
    SVG_PLANE: 'SVG_PLANE',
  },
  SHADER: {
    WATER: 'WATER_SHADER',
  },
  AI_AVATARS: 'AI_AVATARS',
  INITIAL_POINT: 'INITIAL_POINT',
  POI: 'POINT_OF_INTEREST',
  MML_OBJECT: 'MML_OBJECT',
  NPC_AVATARS: 'NPC_AVATARS',
};

export type ShopifyMetadata = {
  id: string;
  storeDomain: string;
  storeVersionAPI: string;
  storeToken: string;
};

export interface ModelProps {
  asset: Asset;
  light?: ILight;
  position?: THREE.Vector3;
  scale?: THREE.Vector3;
  rotation?: THREE.Euler;
  castShadow?: boolean;
  receiveShadow?: boolean;
  instanceId: string;
  showOnMinimap?: boolean;
}

export interface Asset {
  type: string;
  name: string;
  url: string;
  id?: string;
  preview?: string;
  previewImage?: string;
  textureToChange?: string;
  textureToChangeNormal?: string;
  textureToChangeRoughness?: string;
  textureToChangeMetalness?: string;
  showOnMinimap?: false;
  marketId?: number;
  distance?: number;
  isPortal?: Boolean;
  shopifyMetadata?: ShopifyMetadata;
  metadata?: {
    url?: string;
    type?: string;
    [key: string]: any;
  };
  ref: any;
}

const NO_REQUIRED_URL_ASSETS = [
  ASSET_TYPE.MISC.LIGHT,
  ASSET_TYPE.INITIAL_POINT,
  ASSET_TYPE.MML_OBJECT,
  ASSET_TYPE.PRIMITIVE.POSITION_POINT,
  ASSET_TYPE.SHADER.WATER,
];

const urlAudioModel = `${CDN}/Global/ASSETS_STUDIO/IC_Audio.gltf`;
const urlInitialPoint = `${CDN}/Global/ASSETS_STUDIO/spawnmodel.glb`;
const urlBackFaceVideo = `${ASSETS_2022_CDN}/Global/Gui/TEXTURES/EXTRAS/backFaceVideo.jpg`;

export function SceneAsset(props: ModelProps) {
  const {
    asset,
    position,
    scale,
    rotation,
    instanceId,
    castShadow,
    receiveShadow,
    showOnMinimap = false,
  } = props;

  const { ref, ...assetNoRef } = asset;

  if (!isValidURL(asset.url) && !NO_REQUIRED_URL_ASSETS.includes(asset.type)) {
    return <></>;
  }

  const assetURL: string = asset.url?.replace(S3_BUCKET, CDN) || '';

  let gltf;
  switch (asset.type) {
    case ASSET_TYPE.GLTF.TELEPORTFLOOR:
    case ASSET_TYPE.GLTF.HIGHPACK:
    case ASSET_TYPE.GLTF.SIGNPACK:
    case ASSET_TYPE.GLTF.ASSETPACK:
      return (
        <Model
          refer={asset.ref}
          url={assetURL}
          position={position ? position : new THREE.Vector3(0, 0, 0)}
          rotation={rotation}
          name={`${asset.name}${STUDIO_ID_SUFFIX}${instanceId}`}
          castShadow={castShadow}
          receiveShadow={receiveShadow}
        />
      );
    case ASSET_TYPE.AI_AVATARS:
    case ASSET_TYPE.GLTF.OTHER:
      return (
        <Model
          {...assetNoRef}
          dispose={() => {
            gltf.scene.traverse((o) => {
              if (o.geometry) {
                o.geometry.dispose();
              }

              if (o.material) {
                if (o.material.length) {
                  for (let i = 0; i < o.material.length; ++i) {
                    o.material[i].dispose();
                  }
                } else {
                  o.material.dispose();
                }
              }
            });
          }}
          refer={asset.ref}
          url={assetURL}
          position={position}
          scale={scale ? scale : new THREE.Vector3(1, 1, 1)}
          rotation={rotation}
          castShadow={castShadow}
          receiveShadow={receiveShadow}
          name={`${asset.name}${STUDIO_ID_SUFFIX}${instanceId}`}
          isIAAvatar={asset.type === ASSET_TYPE.AI_AVATARS}
          isUploadedAsset={asset.type === ASSET_TYPE.GLTF.OTHER}
        />
      );

    case ASSET_TYPE.PRIMITIVE_TEXTURE.VIDEO_PLANE:
    case ASSET_TYPE.PRIMITIVE_TEXTURE.IMAGE_PLANE:
      const isVideo = asset.type === ASSET_TYPE.PRIMITIVE_TEXTURE.VIDEO_PLANE;
      return (
        <group
          ref={asset.ref}
          key={`asset-${asset.name}-${instanceId}`}
          position={position ? position : [0, 0, 0]}
          scale={scale ? scale : [1, 1, 1]}
          rotation={rotation}
          name={`${asset.name}${STUDIO_ID_SUFFIX}${instanceId}`}
        >
          <mesh>
            <planeGeometry />
            {isVideo ? (
              <>
                <VideoMaterial url={assetURL} />
              </>
            ) : (
              <ImageMaterial url={assetURL} />
            )}
          </mesh>
          <mesh
            position={[0, 0, -0.05]}
            rotation={new THREE.Euler(Math.PI, 0, 0)}
          >
            <planeGeometry />
            <ImageMaterial url={urlBackFaceVideo} />
          </mesh>
        </group>
      );

    case ASSET_TYPE.PRIMITIVE_TEXTURE.SCREEN_SHARE:
      return (
        <group
          ref={asset.ref}
          key={`asset-${asset.name}-${instanceId}`}
          position={position ? position : [0, 0, 0]}
          scale={scale ? scale : [16, 19, 1]}
          rotation={rotation}
          name={`${asset.name}-SCREENSHAREID${STUDIO_ID_SUFFIX}${instanceId}`}
        >
          <mesh>
            <planeGeometry />
            <ImageMaterial url={assetURL} />
          </mesh>
          <mesh
            position={[0, 0, -0.05]}
            rotation={new THREE.Euler(Math.PI, 0, 0)}
          >
            <planeGeometry />
            <ImageMaterial url={urlBackFaceVideo} />
          </mesh>
        </group>
      );
    case ASSET_TYPE.PRIMITIVE_TEXTURE.STREAM:
      const streamURL = asset.metadata?.url || '';
      const streamType = asset.metadata?.type || 'stream';

      const isInEditor =
        typeof window !== 'undefined' &&
        window.location.pathname.includes('/user/studio/scene/');

      const objectName = streamURL
        ? `${
            asset.name
          }-${streamType.toUpperCase()}-STREAM${STUDIO_ID_SUFFIX}${instanceId}`
        : `${asset.name}-STREAM${STUDIO_ID_SUFFIX}${instanceId}`;

      let previewUrl = assetURL;

      if (streamType === 'youtube' && streamURL) {
        const regExp =
          /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = streamURL.match(regExp);
        if (match && match[2].length === 11) {
          previewUrl = `https://img.youtube.com/vi/${match[2]}/maxresdefault.jpg`;
        }
      } else if (streamType === 'twitch' && streamURL) {
        previewUrl = `${ASSETS_2022_CDN}/Global/Gui/TEXTURES/EXTRAS/twitchimg.jpg`;
      }

      const [isPlaying, setIsPlaying] = React.useState(false);

      const handleStreamClick = () => {
        if (isInEditor) {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('stream_editor_notification', {
                detail: {
                  message:
                    'Live streams work only in Preview mode to improve Editor performance. Switch to Preview mode to watch streams.',
                  type: 'info',
                },
              })
            );
          }
          return;
        }

        if (streamURL && !isPlaying) {
          setIsPlaying(true);
        }
      };

      return (
        <group
          ref={asset.ref}
          key={`asset-${asset.name}-${instanceId}`}
          position={position ? position : [0, 0, 0]}
          scale={scale ? scale : [1, 1, 1]}
          rotation={rotation}
          name={objectName}
        >
          <mesh
            onClick={handleStreamClick}
            scale={isInEditor ? [700, 500, 1] : [1, 1, 1]}
          >
            <planeGeometry args={[1, 1]} />
            {isPlaying && streamURL && !isInEditor ? (
              <VideoMaterial url={streamURL} />
            ) : (
              <ImageMaterial url={previewUrl} />
            )}
          </mesh>
          {streamURL && (
            <mesh
              onClick={handleStreamClick}
              position={[0.3, 0.3, 0.01]}
              scale={isInEditor ? [100, 100, 1] : [0.2, 0.2, 1]}
            >
              <planeGeometry args={[1, 1]} />
              <meshBasicMaterial transparent opacity={0.9}>
                <primitive
                  attach='map'
                  object={(() => {
                    const canvas = document.createElement('canvas');
                    canvas.width = 64;
                    canvas.height = 64;
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                      ctx.fillStyle = isInEditor
                        ? 'rgba(255,165,0,0.8)'
                        : 'rgba(0,0,0,0.8)';
                      ctx.beginPath();
                      ctx.arc(32, 32, 30, 0, 2 * Math.PI);
                      ctx.fill();

                      ctx.fillStyle = 'white';

                      if (isInEditor) {
                        ctx.font = 'bold 28px Arial';
                        ctx.textAlign = 'center';
                        ctx.fillText('i', 32, 40);
                      } else {
                        ctx.beginPath();
                        ctx.moveTo(24, 20);
                        ctx.lineTo(24, 44);
                        ctx.lineTo(42, 32);
                        ctx.closePath();
                        ctx.fill();
                      }
                    }
                    return new THREE.CanvasTexture(canvas);
                  })()}
                />
              </meshBasicMaterial>
            </mesh>
          )}
          <mesh
            position={[0, 0, -0.01]}
            rotation={new THREE.Euler(Math.PI, 0, 0)}
            scale={isInEditor ? [700, 500, 1] : [1, 1, 1]}
          >
            <planeGeometry args={[1, 1]} />
            <ImageMaterial url={urlBackFaceVideo} />
          </mesh>
        </group>
      );
    case ASSET_TYPE.PRIMITIVE_TEXTURE.SVG_PLANE:
      return (
        <mesh
          ref={asset.ref}
          key={`asset-${asset.name}-${instanceId}`}
          position={position ? position : [0, 0, 0]}
          scale={scale ? scale : [1, 1, 1]}
          rotation={rotation}
          name={`${asset.name}-ImageID${STUDIO_ID_SUFFIX}${instanceId}`}
        >
          <planeGeometry />
          <ImageMaterial url={assetURL} isImage={false} />
        </mesh>
      );
    case ASSET_TYPE.MISC.POSITIONAL_AUDIO:
      return (
        <Model
          url={urlAudioModel}
          refer={asset.ref}
          position={position ? position : new THREE.Vector3(0, 0, 0)}
          scale={scale ? scale : new THREE.Vector3(2, 2, 2)}
          rotation={rotation}
          name={`PositionalAudioModel${STUDIO_ID_SUFFIX}${asset.name}-${instanceId}`}
        >
          <PositionalAudio
            url={asset.url}
            key={`PositionalAudioElem-${asset.name}-${instanceId}`}
            name={`PositionalAudioElem-${asset.name}`}
            loop
            autoplay
            distance={asset.distance}
          />
        </Model>
      );
    case ASSET_TYPE.MISC.LIGHT:
      if (!props.light) return <></>;
      return <LightAsset {...props} />;

    case ASSET_TYPE.INITIAL_POINT:
      return (
        <Model
          url={urlInitialPoint}
          refer={asset.ref}
          key={`asset-${asset.name}-${instanceId}`}
          position={position ? position : new THREE.Vector3(0, 0, 0)}
          scale={scale ? scale : new THREE.Vector3(2, 2, 2)}
          rotation={rotation}
          name={`${asset.name}${STUDIO_ID_SUFFIX}${instanceId}`}
        />
      );
    case ASSET_TYPE.MML_OBJECT:
      // For MML objects, we use the URL field to store the MML code
      const mmlObject = convertMMLToThreeJS(asset.url);

      return (
        <primitive
          object={mmlObject}
          ref={asset.ref}
          name={`${asset.name}-MML${STUDIO_ID_SUFFIX}${instanceId}`}
          position={position ? position : new THREE.Vector3(0, 0, 0)}
          scale={scale ? scale : new THREE.Vector3(1, 1, 1)}
          rotation={rotation ? rotation : new THREE.Euler(0, 0, 0)}
        />
      );
    case ASSET_TYPE.PRIMITIVE.POSITION_POINT:
      return (
        <SpherePositionPointAsset
          position={position}
          scale={scale}
          rotation={rotation}
          asset={asset}
          instanceId={instanceId}
        />
      );
    case ASSET_TYPE.SHADER.WATER:
      return (
        <WaterShader
          position={position}
          scale={scale}
          rotation={rotation}
          instanceId={instanceId}
          asset={asset}
        />
      );
    default:
      return <></>;
  }
}
